/**
 * AccountPage — /account
 *
 * Sections:
 *  1. KYC verification nudge
 *  2. Referral & Rewards section:
 *     - Loyalty balance card (violet gradient)
 *     - Referral code display + copy link + WhatsApp share
 *     - Stats row (bettors, earned, per-bettor rate)
 *     - Recent rewards (last 5 loyalty transactions)
 *     - "Become a Partner" link
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  getLoyalty,
  getAccountLimits,
  setAccountLimits,
  type LoyaltyResponse,
  type LoyaltyTransaction,
  type AccountLimits,
} from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────

function txTypeLabel(type: string): string {
  switch (type) {
    case 'referral_reward': return 'Referral reward';
    case 'partner_bonus': return 'Partner milestone bonus';
    case 'debit': return 'Debit';
    default: return type.replace(/_/g, ' ');
  }
}

function formatCents(cents: number): string {
  return `R${(cents / 100).toFixed(0)}`;
}

// ── Copy button with 2s "Copied!" feedback ────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(text);
      success = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        success = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        // Both methods failed — don't show "Copied!" feedback
      }
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-2 shrink-0 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ── Referral & Rewards section ────────────────────────────────────────────

function ReferralSection() {
  const [loyalty, setLoyalty] = useState<LoyaltyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getLoyalty()
      .then((r) => setLoyalty(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mt-8 space-y-4">
        <div className="shimmer h-28 w-full rounded-card" />
        <div className="shimmer h-24 w-full rounded-card" />
      </div>
    );
  }

  if (error || !loyalty) {
    return (
      <div className="mt-8 rounded-card bg-error/10 px-4 py-3 text-sm text-error">
        Failed to load rewards info. Please try again.
      </div>
    );
  }

  const whatsappMsg = encodeURIComponent(
    `Join My Bets and get rewarded! Sign up with my code ${loyalty.referral_code}: ${loyalty.referral_link}`,
  );
  const whatsappUrl = `https://wa.me/?text=${whatsappMsg}`;

  const recentTxns: LoyaltyTransaction[] = loyalty.transactions.slice(0, 5);
  const totalEarned = loyalty.transactions
    .filter((t) => t.amount_cents > 0)
    .reduce((sum, t) => sum + t.amount_cents, 0);

  return (
    <section className="mt-8">
      <h2 className="mb-4 font-heading text-2xl font-bold text-text-primary">
        Referral &amp; Rewards
      </h2>

      {/* Loyalty balance card — violet gradient matching WalletPage */}
      <div
        className="mb-6 rounded-card p-6 text-center shadow-card"
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 60%, #f97316 100%)' }}
      >
        <p className="mb-1 text-sm font-medium text-white/80">Rewards Balance</p>
        <p className="font-heading text-4xl font-bold text-white">
          {formatCents(loyalty.balance_cents)}
        </p>
        <p className="mt-1 text-xs text-white/60">
          Loyalty wallet — earn R{loyalty.reward_per_referral / 100} per qualifying bettor
        </p>
      </div>

      {/* Referral code + share */}
      <div className="mb-6 rounded-card border border-border bg-surface p-5 shadow-card">
        <p className="mb-2 text-sm font-medium text-text-secondary">Your referral code</p>
        <div className="mb-4 flex items-center justify-center">
          <span className="rounded-xl bg-violet-600 px-6 py-3 font-mono text-2xl font-bold tracking-widest text-white">
            {loyalty.referral_code}
          </span>
        </div>

        <p className="mb-2 text-sm font-medium text-text-secondary">Share your referral link</p>
        <div className="mb-3 flex items-center">
          <input
            readOnly
            value={loyalty.referral_link}
            className="min-w-0 flex-1 rounded-button border border-border bg-surface-2 px-3 py-2 text-xs text-text-muted outline-none"
          />
          <CopyButton text={loyalty.referral_link} />
        </div>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center gap-2 rounded-button bg-green-500 py-2.5 font-semibold text-white hover:bg-green-600 transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.523 5.847L.057 23.882a.5.5 0 00.607.615l6.204-1.626A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.924 0-3.72-.506-5.27-1.39l-.378-.216-3.921 1.027 1.044-3.813-.23-.382A9.962 9.962 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
          </svg>
          Share on WhatsApp
        </a>
      </div>

      {/* Stats row */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        <div className="rounded-card border border-border bg-surface p-3 text-center shadow-sm">
          <p className="font-heading text-xl font-bold text-violet-600">
            {loyalty.successful_referrals}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">Bettors joined</p>
        </div>
        <div className="rounded-card border border-border bg-surface p-3 text-center shadow-sm">
          <p className="font-heading text-xl font-bold text-violet-600">
            {formatCents(totalEarned)}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">Total earned</p>
        </div>
        <div className="rounded-card border border-border bg-surface p-3 text-center shadow-sm">
          <p className="font-heading text-xl font-bold text-violet-600">
            R{loyalty.reward_per_referral / 100}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">Per bettor</p>
        </div>
      </div>

      {/* Recent rewards */}
      {recentTxns.length > 0 && (
        <div className="mb-6 rounded-card border border-border bg-surface shadow-card">
          <h3 className="border-b border-border px-4 py-3 text-sm font-semibold text-text-primary">
            Recent Rewards
          </h3>
          <ul className="divide-y divide-border">
            {recentTxns.map((tx) => (
              <li key={tx.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-text-primary">{txTypeLabel(tx.type)}</p>
                  {tx.created_at && (
                    <p className="text-xs text-text-muted">
                      {new Date(tx.created_at).toLocaleDateString('en-ZA', {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </p>
                  )}
                </div>
                <span className="font-mono text-sm font-semibold text-success">
                  +{formatCents(tx.amount_cents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Become a Partner CTA */}
      <div className="rounded-card border border-violet-200 bg-violet-50 p-4 text-center">
        <p className="mb-2 text-sm font-medium text-violet-800">
          Share more and earn bigger bonuses
        </p>
        <Link
          to="/partner"
          className="inline-flex items-center gap-1 rounded-button bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
        >
          Become a Partner &#8594;
        </Link>
      </div>
    </section>
  );
}

// ── Deposit & Loss limits (responsible gambling) ───────────────────────────

// Cents (nullable) → Rands string for an input; null/0-less → ''.
function centsToRandStr(cents: number | null): string {
  return cents == null ? '' : (cents / 100).toString();
}

// Rands string → integer cents; '' → undefined (leave unchanged), invalid → null.
function randStrToCents(rands: string): number | null | undefined {
  const t = rands.trim();
  if (t === '') return undefined;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

const LIMIT_ROWS: {
  key: keyof AccountLimits;
  apiKey: 'daily' | 'weekly' | 'monthly' | 'loss_limit_daily' | 'loss_limit_weekly';
  label: string;
}[] = [
  { key: 'deposit_limit_daily_cents', apiKey: 'daily', label: 'Daily deposit limit' },
  { key: 'deposit_limit_weekly_cents', apiKey: 'weekly', label: 'Weekly deposit limit' },
  { key: 'deposit_limit_monthly_cents', apiKey: 'monthly', label: 'Monthly deposit limit' },
  { key: 'loss_limit_daily_cents', apiKey: 'loss_limit_daily', label: 'Daily loss limit' },
  { key: 'loss_limit_weekly_cents', apiKey: 'loss_limit_weekly', label: 'Weekly loss limit' },
];

function LimitsSection() {
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const hydrate = (limits: AccountLimits) => {
    const next: Record<string, string> = {};
    for (const row of LIMIT_ROWS) {
      next[row.apiKey] = centsToRandStr(limits[row.key]);
    }
    setForm(next);
  };

  useEffect(() => {
    getAccountLimits()
      .then((r) => hydrate(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    const body: Record<string, number> = {};
    for (const row of LIMIT_ROWS) {
      const cents = randStrToCents(form[row.apiKey] ?? '');
      if (cents === null) {
        setMsg('Limits must be a valid amount (0 or more).');
        setSaving(false);
        return;
      }
      if (cents !== undefined) body[row.apiKey] = cents;
    }
    try {
      const res = await setAccountLimits(body);
      const deferred = res.data.deferred.length > 0;
      setMsg(
        deferred
          ? 'Saved. Increases take effect in 24 hours; any decreases applied immediately.'
          : 'Limits saved.',
      );
      setTimeout(() => setMsg(null), 5000);
    } catch {
      setMsg('Failed to save limits. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="mt-8">
        <div className="shimmer h-40 w-full rounded-card" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="mt-8 rounded-card bg-error/10 px-4 py-3 text-sm text-error">
        Failed to load your limits. Please try again.
      </section>
    );
  }

  return (
    <section className="mt-8">
      <h2 className="mb-2 font-heading text-2xl font-bold text-text-primary">
        Deposit &amp; Loss Limits
      </h2>
      <p className="mb-4 text-sm text-text-muted">
        Set your own limits to stay in control. Leave a field blank for no limit. Lowering a
        limit applies immediately; raising one takes effect after 24 hours.
      </p>

      <div className="rounded-card border border-border bg-surface p-5 shadow-card">
        <div className="space-y-4">
          {LIMIT_ROWS.map((row) => (
            <div key={row.apiKey} className="flex items-center justify-between gap-3">
              <label htmlFor={`limit-${row.apiKey}`} className="text-sm font-medium text-text-secondary">
                {row.label}
              </label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-text-muted">R</span>
                <input
                  id={`limit-${row.apiKey}`}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="No limit"
                  value={form[row.apiKey] ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, [row.apiKey]: e.target.value }))}
                  className="w-32 rounded-button border border-border bg-surface-2 px-3 py-2 text-sm text-text-primary outline-none focus:border-violet-500"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-button bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save limits'}
          </button>
          {msg && <span className="text-sm text-text-secondary">{msg}</span>}
        </div>
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function AccountPage() {
  const { user } = useAuth();

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 mx-auto max-w-2xl w-full px-4 py-12 sm:px-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-6">Account</h1>

        {user && user.kyc_tier < 2 && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <span className="text-amber-600">&#9888;</span>
            <span className="text-sm text-amber-800 font-medium">Verify your identity to unlock withdrawals</span>
            <Link to="/kyc" className="ml-auto text-sm font-bold text-violet-700 hover:underline">Verify Now &#8594;</Link>
          </div>
        )}

        <LimitsSection />

        <ReferralSection />
      </main>
      <Footer />
    </div>
  );
}
